import { useApi } from "./useApi"
import {
	GetOwnerUrl,
	GetOwnerByIdUrl,
	UpdateOwnerUrl,
	DeleteOwnerUrl,
	CreateOwnerUrl,
	GetPropertiesWithNoOwnerUrl,
	GetRentalAndExpensesByOwnerIdUrl,
	GetOwnerFinancialSummaryUrl,
	GetOwnerReservationsUrl,
	GetOwnerExpensesUrl,
	AddOwnerUserUrl
} from "../utils/constants"
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query"


const useOwnerManagment = () => {
	const { apiPrivate } = useApi()
	/* ------------------------------ getOwners ----------------------------- */
	const getOwners = async (params: {
		pageSize: string
		pageNumber: string
		search?: string
	}) => {
		const response: { data: any } = await apiPrivate.get(
			GetOwnerUrl + `?pageSize=${params.pageSize}&pageNumber=${params.pageNumber}`)
		return response
	}

	const GetOwnersQuery = (data: {
		pageSize: string
		pageNumber: string
		search?: string
	}) =>
		useQuery({
			queryKey: [GetOwnerUrl, data],
			queryFn: () => getOwners(data),
		})
	/* ------------------------------ getPropertiesWithNoOwners ----------------------------- */
	const getPropertiesWithNoOwners = async (params: {
		pageSize: string
		pageNumber: string
		search?: string
	}) => {
		const response: { data: any } = await apiPrivate.get(
			GetPropertiesWithNoOwnerUrl + `?pageSize=${params?.pageSize}&pageNumber=${params?.pageNumber}`)
		return response
	}

	const getPropertiesWithNoOwnersQuery = (data: {
		pageSize: string
		pageNumber: string
		search?: string
	}) =>
		useQuery({
			queryKey: [GetPropertiesWithNoOwnerUrl, data],
			queryFn: () => getPropertiesWithNoOwners(data),
		})
	/* ------------------------------ GetRentalAndExpensesByOwnerId ----------------------------- */
	const GetRentalAndExpensesByOwnerId = async (id: string) => {
		const response: { data: any } = await apiPrivate.get(
			GetRentalAndExpensesByOwnerIdUrl + `/${id}`)
		return response
	}

	const GetRentalAndExpensesByOwnerIdQuery = (id: string) =>
		useQuery({
			queryKey: [GetRentalAndExpensesByOwnerIdUrl, id],
			queryFn: () => GetRentalAndExpensesByOwnerId(id),
		})
	/* ------------------------------ GetOwnerFinancialSummary ----------------------------- */
	const GetOwnerFinancialSummary = async (id: string) => {
		const response: { data: any } = await apiPrivate.get(
			GetOwnerFinancialSummaryUrl + `/${id}`)
		return response
	}

	const GetOwnerFinancialSummaryQuery = (id: string, options?: Partial<UseQueryOptions>) =>
		useQuery<any>({
			queryKey: [GetOwnerFinancialSummaryUrl, id],
			queryFn: () => GetOwnerFinancialSummary(id),
			...options
		})
	/* ------------------------------ GetOwnerReservations ----------------------------- */
	const GetOwnerReservations = async (params: {
		ownerId: string
		pageSize: number
		pageNumber: number
	}) => {
		const response: { data: any } = await apiPrivate.get(
			GetOwnerReservationsUrl + `/${params.ownerId}?pageSize=${params.pageSize}&pageNumber=${params.pageNumber}`)
		return response
	}

	const GetOwnerReservationsQuery = (data: {
		ownerId: string
		pageSize: number
		pageNumber: number
	}, options?: Partial<UseQueryOptions>) =>
		useQuery<any>({
			queryKey: [GetOwnerReservationsUrl, data],
			queryFn: () => GetOwnerReservations(data),
			...options
		})
	/* ------------------------------ GetOwnerExpenses ----------------------------- */
	const GetOwnerExpenses = async (params: {
		ownerId: string
		pageSize: number
		pageNumber: number
	}) => {
		const response: { data: any } = await apiPrivate.get(
			GetOwnerExpensesUrl + `/${params.ownerId}?pageSize=${params.pageSize}&pageNumber=${params.pageNumber}`)
		return response
	}

	const GetOwnerExpensesQuery = (data: {
		ownerId: string
		pageSize: number
		pageNumber: number
	}, options?: Partial<UseQueryOptions>) =>
		useQuery<any>({
			queryKey: [GetOwnerExpensesUrl, data],
			queryFn: () => GetOwnerExpenses(data),
			...options
		})
	/* ------------------------- GetOwnerByID ------------------------- */
	const getOwnerByID = async (ownerId: string) => {
		const response: { data: any } = await apiPrivate.get(
			`${GetOwnerByIdUrl}/${ownerId}`
		)

		return response
	}

	const GetOwnerByIdQuery = (ownerId: string) =>
		useQuery({
			queryKey: [GetOwnerByIdUrl, ownerId],
			queryFn: () => getOwnerByID(ownerId),
		})


	/* -------------------------- AddOwner -------------------------- */
	const AddOwner = async (body: any) => {
		const data: any = await apiPrivate.post(
			CreateOwnerUrl,
			body
		)
		return data
	}

	const CreateOwnerMutate = ({
		onError,
	}: {
		onError?: (error: any) => void
	}) =>
		useMutation({
			mutationFn: (body: any) => AddOwner(body),
			onError: (error) => onError && onError(error),
		})
	/* -------------------------- updateOwner -------------------------- */
	const updateOwner = async (body: any) => {
		const data: any = await apiPrivate.put(
			`${UpdateOwnerUrl}/${body.id}`,
			body
		)
		return data
	}

	const UpdateOwnerMutate = ({ onError }: {
		onError?: (error: any) => void
	}) =>
		useMutation({
			mutationFn: (body: any) => updateOwner(body),
			onError: (error) => onError && onError(error),
		})

	/* ----------------------------- Delete owner ---------------------------- */
	const deleteOwner = async (id: number) => {
		const data: any = await apiPrivate.delete(
			`${DeleteOwnerUrl}/${id}`
		)
		return data
	}
	const DeleteOwnerMutate = () =>
		useMutation({
			mutationFn: (id: number) => deleteOwner(id),
		})

	/* -------------------------- AddOwnerUser -------------------------- */
	const AddOwnerUser = async (body: { ownerId: string; email: string }) => {
		const data: any = await apiPrivate.post(
			AddOwnerUserUrl,
			body
		)
		return data
	}

	const AddOwnerUserMutate = ({
		onSuccess,
		onError,
	}: {
		onSuccess?: (data: any) => void
		onError?: (error: any) => void
	}) =>
		useMutation({
			mutationFn: (body: { ownerId: string; email: string }) => AddOwnerUser(body),
			onSuccess: (data) => onSuccess && onSuccess(data),
			onError: (error) => onError && onError(error),
		})

	return {
		GetOwnersQuery,
		CreateOwnerMutate,
		DeleteOwnerMutate,
		GetOwnerByIdQuery,
		UpdateOwnerMutate,
		getPropertiesWithNoOwnersQuery,
		GetRentalAndExpensesByOwnerIdQuery,
		GetOwnerFinancialSummaryQuery,
		GetOwnerReservationsQuery,
		GetOwnerExpensesQuery,
		AddOwnerUserMutate
	}
}
export { useOwnerManagment }
